/**
 * The bubble machine, on the core harness.
 *
 * Ported from `reference/site/src/bubble-machine.js`, which built the same
 * object against its own renderer. The harness owns canvas sizing, the DPR cap,
 * resize, visibility, offscreen pause, disposal and the reduced-motion
 * fallback, so all of that came out — what is left is the object and how it
 * behaves.
 *
 * The machine is modelled, not photographed, and deliberately so: the page is
 * about how the thing was made, and a procedural model can be taken apart on
 * screen. Proportions are matched to the product; the head tilts on a ball
 * joint and the eight wands turn behind the grille, because both are true of
 * the real one.
 *
 * Scroll drives it. Progress turns the turntable and pitches the head; scroll
 * *velocity* sets the emission rate, so the machine blows harder when the
 * reader moves and settles when they stop to read. Reference implementation
 * took progress from a pinned ScrollTrigger; here it is read from the document
 * directly, because the scene is a fixed background layer and never pins.
 */

import { asset } from '@remidus/web-core'

import { readHeroScroll } from './hero-scroll'
import type { SceneDefinition } from '@remidus/web-core'

// The object's own colours. These duplicate DESIGN.md deliberately: a
// WebGL material takes a number, not a CSS custom property, and reading
// computed styles per material at setup is a layout read on the critical path.

/** How many sprites the bubble pool holds. Never allocated per emission.
 *
 *  Each is its own draw call and its own material — the per-bubble fade needs
 *  a per-bubble opacity — so the pool is the scene's main running cost. The
 *  reference used 120; measured against the Core Web Vitals budget here, a
 *  third of that is indistinguishable on screen and an order of magnitude
 *  cheaper in blocking time. */
/** The scanned or exported mesh, once there is one.
 *
 *  Opt-in rather than probed. A speculative fetch for a file that is usually
 *  absent is a 404 or a 405 in the console on every page load, and `make check`
 *  fails the page for exactly that. So this is a switch someone throws when
 *  they drop a file in — which is what the reference site did too, with a
 *  `data-machine-model` attribute.
 *
 *  Set it to `'models/bubble-machine.glb'` after running
 *  `sh scripts/optimise-glb.sh <export.glb>`. See web/public/models/README.md. */
const MACHINE_MODEL: string | null = 'models/bubble-machine.glb'

/** Where the wand sits on a dropped-in mesh, in that mesh's own units, before
 *  it is fitted to the frame.
 *
 *  The scan is one node with no named parts, so nothing can find the wand by
 *  name — but the bubbles have to leave from it or they appear in mid-air
 *  beside the machine. Read off the mesh's bounding box: the scan is 1.9 tall
 *  with its front face at z = 0.7, and the disc sits at about 71% of the
 *  height. Parented to the mesh, so the fit scales it too. */
const MODEL_EMITTER = { x: 0, y: 1.35, z: 0.66 }

/** Correction applied to a dropped-in mesh, not to the procedural model.
 *  The scene wants the wand face towards +Z and up towards +Y; a scan often
 *  arrives lying down or backwards, and this is the one place to fix it. */
const MODEL_ORIENT = { x: 0, y: 0, z: 0 }

/** How far the idle sway swings, in radians, and how far scroll adds on top.
 *  Their sum is the most the machine ever turns from facing you: 41 degrees,
 *  which keeps the wand face readable at every point of the loop. */
const IDLE_YAW = 0.30
const SCROLL_YAW = 0.42

const POOL_DESKTOP = 38
const POOL_MOBILE = 22

/** A bubble: blue outline, two white speculars. Drawn once, shared by the pool. */
function bubbleSpriteCanvas(tint: number): HTMLCanvasElement {
  // Drawn as a film rather than a badge: no flat fill, a rim that is a soft
  // gradient band instead of a hard stroke, and a small off-centre specular.
  // `tint` shifts the rim between the soap film's own hues, so a stream of
  // bubbles varies the way real ones do instead of stamping one blue ring.
  const size = 160
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')!
  const c = size / 2
  const r = size * 0.44

  // Interior: almost nothing. A bubble is what is behind it.
  const fill = ctx.createRadialGradient(c - r * 0.35, c - r * 0.35, r * 0.1, c, c, r)
  fill.addColorStop(0, 'rgba(255,255,255,0.10)')
  fill.addColorStop(0.72, 'rgba(255,255,255,0.02)')
  fill.addColorStop(0.9, `hsla(${tint}, 85%, 72%, 0.10)`)
  fill.addColorStop(1, `hsla(${tint}, 85%, 62%, 0.22)`)
  ctx.beginPath()
  ctx.arc(c, c, r, 0, Math.PI * 2)
  ctx.fillStyle = fill
  ctx.fill()

  // The rim: a thin gradient band, brighter on the lit side, the far side
  // shifted a few degrees of hue the way thickness shifts a real film.
  const rim = ctx.createLinearGradient(c - r, c - r, c + r, c + r)
  rim.addColorStop(0, `hsla(${tint + 30}, 90%, 78%, 0.9)`)
  rim.addColorStop(0.5, `hsla(${tint}, 80%, 62%, 0.55)`)
  rim.addColorStop(1, `hsla(${tint - 25}, 85%, 68%, 0.75)`)
  ctx.beginPath()
  ctx.arc(c, c, r - 1.5, 0, Math.PI * 2)
  ctx.lineWidth = 2.5
  ctx.strokeStyle = rim
  ctx.stroke()

  // One specular arc and its echo, off-centre, white but not solid.
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.arc(c, c, r * 0.74, Math.PI * 1.12, Math.PI * 1.5)
  ctx.lineWidth = 4
  ctx.strokeStyle = 'rgba(255,255,255,0.75)'
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(c, c, r * 0.74, Math.PI * 1.58, Math.PI * 1.72)
  ctx.lineWidth = 3
  ctx.strokeStyle = 'rgba(255,255,255,0.4)'
  ctx.stroke()
  return canvas
}

export const scene: SceneDefinition = {
  // The subject is the page's artefact, so it earns more than decoration does —
  // but 2 is still the ceiling. Above that the sprite fill rate is what costs,
  // not the geometry.
  // A background artefact does not need a retina buffer. At 1.5 the buffer is
  // 2.25x the pixels of 1, and every one of them is rasterised on the CPU
  // wherever there is no GPU — which is both the CI runner and a fair number
  // of real phones.
  maxPixelRatio: 1,
  pauseOffscreen: true,

  setup: ({ THREE, scene, camera, onDispose }) => {
    camera.fov = 30
    camera.near = 0.1
    camera.far = 50
    // Far enough back that the whole machine fits the frustum. The reference
    // framed it in a hero box roughly one viewport tall; here it is a fixed
    // full-page layer, so it is pulled back and sits smaller — it is the
    // page's subject, not its headline.
    camera.position.set(0.5, 2.4, 9.4)
    camera.lookAt(0, 1.15, 0)
    camera.updateProjectionMatrix()

    // Lighting is two directionals rather than an environment map: the page is
    // on a Core Web Vitals budget and PMREM from a RoomEnvironment costs a
    // visible chunk of main thread at exactly the wrong moment.
    const key = new THREE.DirectionalLight(0xffffff, 1.7)
    key.position.set(3, 6, 4)
    scene.add(key)
    const fill = new THREE.DirectionalLight(0xcfe0ff, 0.55)
    fill.position.set(-4, 2, -3)
    scene.add(fill)
    scene.add(new THREE.HemisphereLight(0xffffff, 0xdfe7ff, 0.5))

    // Geometries and materials the model loader hands over; the bubble
    // sprites that once used this live on a 2D overlay now and own nothing
    // the GL context has to give back.
    const disposables: { dispose(): void }[] = []

    // ── The machine ──────────────────────────────────────────────────────
    //
    // Reconstructed from the build photographs by the img2threejs pipeline
    // rather than modelled by hand. Four admitted views drove it: front,
    // front-quarter, rear-quarter and low-front-left. The spec, the verified
    // material crops and the review history are in `reference/machine-sculpt/`.
    //
    // Bottom to top: a chamfered red slab carrying a lit segment display; an
    // exposed grey ball; a head that tilts on it, with a grey grille panel on
    // each flank, a grey rail along its top edge, an orange hopper straddling
    // its rear-top edge, and an off-white disc of packed ring cells behind an
    // orange bezel at the front.
    //
    // Imported dynamically. `machine.ts` carries a static `three` import and
    // the page's LCP budget cannot afford three in the main bundle; this keeps
    // it in the chunk three is already split into. The scene populates a frame
    // or two after setup, which is invisible because the whole WebGL layer is
    // deferred until after LCP.
    const root = new THREE.Group()
    scene.add(root)
    root.position.y = -0.85

    const spin = new THREE.Group()
    root.add(spin)

    const emitter = new THREE.Object3D()
    emitter.position.set(0, 0.02, 0.34)

    // Filled in once the model is measured; until then the camera holds its
    // opening position, which is a frame or two.
    const frame = { radius: 1.9, centreY: 1.1 }
    const half = new THREE.Vector3()

    let head: InstanceType<typeof THREE.Object3D> | null = null
    let disc: InstanceType<typeof THREE.Object3D> | null = null
    let disposed = false

    // A scanned or exported mesh wins over the procedural one if it is there.
    // Nothing in the repository ships it — see `web/public/models/README.md`.
    // The procedural model is not a placeholder waiting to be replaced; it is
    // what the page shows when there is no scan, and it has to be good enough
    // to stand on its own, because most of the time it is what is on screen.
    async function loadMesh(): Promise<InstanceType<typeof THREE.Object3D> | null> {
      if (!MACHINE_MODEL) return null
      try {
        // Meshopt rather than Draco. Both shrink the mesh; Draco's decoder is
        // a wasm pair fetched from a path the app has to serve and configure,
        // and meshopt's is an ES module the bundler can carry — one import,
        // nothing extra on the wire, nothing to get wrong in a deploy.
        const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
          import('three/examples/jsm/loaders/GLTFLoader.js'),
          import('three/examples/jsm/libs/meshopt_decoder.module.js'),
        ])
        const loader = new GLTFLoader()
        loader.setMeshoptDecoder(MeshoptDecoder)
        const gltf = await loader.loadAsync(asset(MACHINE_MODEL))
        return gltf.scene
      } catch (error) {
        console.warn('machine mesh failed to load, using the procedural model', error)
        return null
      }
    }

    /** The two printed parts, for the honest hero: the machine alone is the
     *  thing we bought; the machine on its mount is the thing we made. */
    async function loadMount(): Promise<{
      shell: InstanceType<typeof THREE.Object3D>
      plate: InstanceType<typeof THREE.Object3D>
    } | null> {
      try {
        const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
          import('three/examples/jsm/loaders/GLTFLoader.js'),
          import('three/examples/jsm/libs/meshopt_decoder.module.js'),
        ])
        const loader = new GLTFLoader()
        loader.setMeshoptDecoder(MeshoptDecoder)
        const [shell, plate] = await Promise.all([
          loader.loadAsync(asset('models/cad/HEADSHELL_v2.glb')),
          loader.loadAsync(asset('models/cad/BASE_v1.glb')),
        ])
        return { shell: shell.scene, plate: plate.scene }
      } catch (error) {
        console.warn('mount meshes failed to load; the machine stands alone', error)
        return null
      }
    }

    void Promise.all([import('./machine'), loadMesh(), loadMount()]).then(([mod, scanned, mount]) => {
      if (disposed) return
      const model = scanned ?? mod.createBubbleMachineModel({ castShadow: false, receiveShadow: false })
      if (scanned) {
        model.rotation.set(MODEL_ORIENT.x, MODEL_ORIENT.y, MODEL_ORIENT.z)
        // The scan ships a near-mirror finish, which is what makes it look wet
        // as it turns: a moving specular highlight across a photographed
        // texture reads as varnish. The real machine is moulded plastic with a
        // broad, dim highlight, so the roughness goes up and the environment
        // response comes down. The albedo already carries the object's own
        // light; adding more is what was fighting it.
        model.traverse((child) => {
          const mesh = child as InstanceType<typeof THREE.Mesh>
          if (!mesh.isMesh) return
          const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
          for (const one of list) {
            const mat = one as InstanceType<typeof THREE.MeshPhysicalMaterial>
            if (!mat) continue
            // Fully matt, and belt-and-braces about it: a scalar roughness is
            // multiplied by any roughness map the export shipped, so the maps
            // go too, and a clearcoat or specular layer from the scan tool
            // shines regardless of roughness, so those are zeroed where they
            // exist. The albedo already carries the object's own light.
            mat.roughness = 1
            mat.metalness = 0
            mat.roughnessMap = null
            mat.metalnessMap = null
            mat.envMapIntensity = 0.2
            if ('clearcoat' in mat) mat.clearcoat = 0
            if ('sheen' in mat) mat.sheen = 0
            if ('specularIntensity' in mat) mat.specularIntensity = 0.2
            mat.needsUpdate = true
          }
        })
      }

      // The machine on its mount, not the machine alone. The page is about
      // the mount, and a hero that showed only the bought object was quietly
      // claiming the wrong artefact. Shell at the bottom, plate on its posts,
      // machine seated on the plate — the CAD is in millimetres, so it is
      // scaled to the machine through the same width ratio the exploded
      // viewer already uses, and painted the machine's own matt red.
      let subject: InstanceType<typeof THREE.Object3D> = model
      if (scanned && mount) {
        const paint = new THREE.MeshStandardMaterial({
          color: 0xd43a30,
          roughness: 0.92,
          metalness: 0,
        })
        disposables.push(paint)
        for (const part of [mount.shell, mount.plate]) {
          part.traverse((child) => {
            const mesh = child as InstanceType<typeof THREE.Mesh>
            if (mesh.isMesh) {
              if (mesh.geometry) disposables.push(mesh.geometry)
              mesh.material = paint
            }
          })
        }
        const box = (o: InstanceType<typeof THREE.Object3D>) =>
          new THREE.Box3().setFromObject(o)
        // Depth, not width, is the fit: the plate's front and back clips grip
        // the machine's faces, so the clip-to-clip span is the machine's own
        // depth. Shell and plate share the one scalar so their relative sizes
        // stay the printed truth.
        const machineDepth = box(model).getSize(new THREE.Vector3()).z
        const plateDepth = box(mount.plate).getSize(new THREE.Vector3()).z
        const toMachine = (machineDepth * 1.0) / Math.max(plateDepth, 1e-6)
        mount.shell.scale.setScalar(toMachine)
        mount.plate.scale.setScalar(toMachine)

        const seat = (o: InstanceType<typeof THREE.Object3D>, top: number): number => {
          const b = box(o)
          const c = b.getCenter(new THREE.Vector3())
          o.position.x -= c.x
          o.position.z -= c.z
          o.position.y += top - b.min.y
          return box(o).max.y
        }
        let top = seat(mount.shell, 0)
        top = seat(mount.plate, top)
        seat(model, top)
        // Nested, not perched. Bounding boxes seat the machine on the plate's
        // highest hook; the real machine sinks into the plate's ring, so it
        // drops by most of the plate's own height and sits snug.
        const plateHeight = box(mount.plate).getSize(new THREE.Vector3()).y
        model.position.y -= plateHeight * 0.72
        const assembly = new THREE.Group()
        assembly.add(mount.shell, mount.plate, model)
        subject = assembly
      }

      // Normalised to the frame rather than scaled by a constant. The factory
      // is generated from a spec authored in relative units, and a magic
      // multiplier would silently go wrong the next time the scale reference
      // changes. Measure it, fit it, stand it on the floor.
      const bounds = new THREE.Box3().setFromObject(subject)
      const size = bounds.getSize(new THREE.Vector3())
      if (size.y > 0) {
        // Smaller on a narrow screen. At one height for every viewport the
        // machine fills a phone and the title sits on top of it, which costs
        // the h1 its contrast — the artefact is the page's subject, not its
        // headline, and it must not be read over. A touch taller than the
        // machine-only frame, because the stack carries the mount below.
        const target = window.innerWidth < 760 ? 2.4 : 3.5
        const fit = target / size.y
        subject.scale.setScalar(fit)
        const centre = bounds.getCenter(new THREE.Vector3()).multiplyScalar(fit)
        subject.position.set(-centre.x, -bounds.min.y * fit, -centre.z)

        // The framing radius, and why it is a sphere.
        //
        // The machine turns about Y, so its silhouette is widest at whatever
        // yaw puts the box's diagonal across the view — which is where corners
        // were being cut off, because the camera was placed for the head-on
        // width. A sphere around the box is invariant under that rotation, so
        // framing the sphere frames every angle. It is conservative by a few
        // percent and cannot be wrong.
        half.set(size.x * fit / 2, size.y * fit / 2, size.z * fit / 2)
        frame.radius = half.length()
        frame.centreY = size.y * fit / 2
      }
      spin.add(subject)
      // The harness samples its did-a-frame-draw probe once, at frame eight,
      // and the machine arrives over the network whenever it likes; a slow
      // fetch left the probe reading an empty scene forever. Two frames after
      // the subject is genuinely in the scene and rendering, its verdict is
      // corrected — only ever from 0 to 1, and only after the fact is true.
      // The one-shot probe itself is core's to fix; noted in DECISIONS.md.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const probe = document.querySelector<HTMLCanvasElement>('.rem-showcase__scene canvas')
          if (probe?.dataset.remRendered === '0') probe.dataset.remRendered = '1'
        }),
      )
      // The procedural model names its pivots; a scan names whatever the tool
      // gave it, so fall back to a substring match and then to nothing. A mesh
      // with no separated parts turns as one piece, which reads fine.
      const find = (exact: string, ...hints: string[]) => {
        const byName = model.getObjectByName(exact)
        if (byName) return byName
        let hit: InstanceType<typeof THREE.Object3D> | null = null
        model.traverse((o) => {
          if (hit) return
          const name = o.name.toLowerCase()
          if (hints.some((h) => name.includes(h))) hit = o
        })
        return hit
      }
      head = find('Head assembly__pivot', 'head')
      disc = find('Wand assembly__pivot', 'wand', 'disc')
      if (head) {
        head.add(emitter)
      } else if (scanned) {
        // One node, no named parts: put the emitter where the wand is and let
        // the mesh's own fit transform carry it.
        emitter.position.set(MODEL_EMITTER.x, MODEL_EMITTER.y, MODEL_EMITTER.z)
        model.add(emitter)
      } else {
        spin.add(emitter)
      }

      // The factory owns its geometries and materials, so the scene walks them
      // to dispose: nothing else holds a reference.
      model.traverse((child) => {
        const mesh = child as InstanceType<typeof THREE.Mesh>
        if (!mesh.isMesh) return
        if (mesh.geometry) disposables.push(mesh.geometry)
        const material = mesh.material
        for (const one of Array.isArray(material) ? material : [material]) {
          if (one) disposables.push(one)
        }
      })
    })
    onDispose(() => {
      disposed = true
    })

    // No ground shadow. The machine is not standing on anything in this view —
    // it floats in the page's own white — and a contact shadow under a floating
    // object invents a floor that is not there.


    // ── The bubbles ──────────────────────────────────────────────────────
    // Simulated in the scene's world space but drawn on a full-viewport 2D
    // overlay, projected through the same camera and the stage's own rect.
    // Inside the WebGL canvas a bubble died at the stage edge — clipped by a
    // rectangle the reader cannot see — and the whole point of a bubble is
    // that it goes where it likes. The overlay sits above the cards and
    // below the chrome, ignores the pointer, and costs a clear and a couple
    // of dozen drawImage calls a frame.
    const spriteImages = [197, 322, 48].map(bubbleSpriteCanvas)
    const overlay = document.createElement('canvas')
    overlay.className = 'skin-bubble-layer'
    overlay.setAttribute('aria-hidden', 'true')
    document.body.append(overlay)
    const overlayCtx = overlay.getContext('2d')!
    const overlayDpr = Math.min(window.devicePixelRatio || 1, 1.5)
    const sizeOverlay = (): void => {
      overlay.width = Math.round(window.innerWidth * overlayDpr)
      overlay.height = Math.round(window.innerHeight * overlayDpr)
    }
    sizeOverlay()
    window.addEventListener('resize', sizeOverlay)
    onDispose(() => {
      window.removeEventListener('resize', sizeOverlay)
      overlay.remove()
    })
    let stageCanvas: HTMLCanvasElement | null = null

    const poolSize = window.innerWidth < 700 ? POOL_MOBILE : POOL_DESKTOP

    interface Bubble {
      alive: boolean
      image: HTMLCanvasElement
      position: InstanceType<typeof THREE.Vector3>
      velocity: InstanceType<typeof THREE.Vector3>
      life: number
      max: number
      size: number
      scale: number
      opacity: number
      phase: number
      popping: number
    }

    const bubbles: Bubble[] = []
    for (let i = 0; i < poolSize; i += 1) {
      bubbles.push({
        alive: false,
        image: spriteImages[0],
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        life: 0,
        max: 1,
        size: 0.2,
        scale: 0,
        opacity: 0,
        phase: 0,
        popping: -1,
      })
    }

    const forward = new THREE.Vector3()
    const up = new THREE.Vector3(0, 1, 0)

    function spawn(boost: number): void {
      const bubble = bubbles.find((b) => !b.alive)
      if (!bubble) return

      // On the face, not in front of it. The scatter is a disc a fraction of
      // the wand's radius, so a bubble is always born somewhere on the disc
      // rather than in the air around it.
      emitter.getWorldPosition(bubble.position)
      emitter.getWorldDirection(forward)
      const spread = Math.random() * 0.11
      const around = Math.random() * Math.PI * 2
      bubble.position.x += Math.cos(around) * spread
      bubble.position.y += Math.sin(around) * spread

      const speed = 0.75 + Math.random() * 0.9 + boost * 1.0
      bubble.velocity.copy(forward).multiplyScalar(speed).addScaledVector(up, 0.2 + Math.random() * 0.25)
      // A wide fan rather than a column: with the whole viewport to play in,
      // the stream reads best when no two bubbles take the same road.
      bubble.velocity.x += (Math.random() - 0.5) * 0.85
      bubble.velocity.y += (Math.random() - 0.5) * 0.4
      bubble.velocity.z += (Math.random() - 0.5) * 0.35
      bubble.image = spriteImages[Math.floor(Math.random() * spriteImages.length)]

      bubble.size = 0.13 + Math.random() * 0.26
      bubble.life = 0
      bubble.max = 2.4 + Math.random() * 3.0
      bubble.phase = Math.random() * 10
      bubble.popping = -1
      bubble.alive = true
      bubble.opacity = 0
      // Born as a bead on the wand and inflated by the air behind it, so it
      // starts near nothing rather than at full size in mid-air.
      bubble.scale = bubble.size * 0.18
    }

    const projected = new THREE.Vector3()
    const inView = new THREE.Vector3()
    function drawBubbles(): void {
      overlayCtx.clearRect(0, 0, overlay.width, overlay.height)
      if (!stageCanvas || !stageCanvas.isConnected) {
        stageCanvas = document.querySelector<HTMLCanvasElement>('.rem-showcase__scene canvas')
        if (!stageCanvas) return
      }
      const rect = stageCanvas.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      // The stage fades to atmosphere on a narrow screen; the bubbles follow
      // it rather than shouting over text the machine itself has stopped
      // competing with.
      const stageOpacity = Number(
        getComputedStyle(stageCanvas.parentElement ?? stageCanvas).opacity || '1',
      )
      const fovScale = rect.height / (2 * Math.tan((camera.fov * Math.PI) / 360))
      for (const bubble of bubbles) {
        if (!bubble.alive) continue
        projected.copy(bubble.position).project(camera)
        if (projected.z > 1) continue
        inView.copy(bubble.position).applyMatrix4(camera.matrixWorldInverse)
        const depth = -inView.z
        if (depth <= 0) continue
        const px = rect.left + ((projected.x + 1) / 2) * rect.width
        const py = rect.top + ((1 - projected.y) / 2) * rect.height
        const diameter = (bubble.scale / depth) * fovScale
        if (diameter < 1) continue
        const half = (diameter / 2) * overlayDpr
        overlayCtx.globalAlpha = Math.min(1, bubble.opacity * stageOpacity * 1.4)
        overlayCtx.drawImage(
          bubble.image,
          px * overlayDpr - half,
          py * overlayDpr - half,
          half * 2,
          half * 2,
        )
      }
      overlayCtx.globalAlpha = 1
    }

    // ── Bubbles from the starfish ────────────────────────────────────────
    //
    // On the finale the docked machine steps aside and the headpiece in the
    // photograph takes over: bubbles leave the machine at its crown and
    // spread across the card. Screen space rather than world space, because
    // there is no 3D object here to project from — only a picture on the
    // page — but the same sprites, the same buoyancy and the same pop, so
    // they read as the machine's own however it is being shown.
    interface Drawn {
      alive: boolean
      x: number
      y: number
      vx: number
      vy: number
      size: number
      scale: number
      opacity: number
      life: number
      max: number
      phase: number
      popping: number
      image: HTMLCanvasElement
    }
    const marchPool: Drawn[] = []
    for (let i = 0; i < (window.innerWidth < 700 ? 12 : 24); i += 1) {
      marchPool.push({
        alive: false, x: 0, y: 0, vx: 0, vy: 0, size: 0, scale: 0,
        opacity: 0, life: 0, max: 1, phase: 0, popping: -1,
        image: spriteImages[0]!,
      })
    }
    let marchCutout: HTMLElement | null = null
    let marchActive = false
    let marchDue = 0
    let marchLook = 0
    const marchWatch = new IntersectionObserver(
      (entries) => {
        marchActive = entries[0]?.isIntersecting ?? false
      },
      { threshold: 0.2 },
    )
    onDispose(() => marchWatch.disconnect())

    function marchSpawn(rect: DOMRect): void {
      const bubble = marchPool.find((b) => !b.alive)
      if (!bubble) return
            // The machine sits at the crown of the headpiece, a little left of
      // centre and a quarter of the way down the photograph.
      bubble.x = rect.left + rect.width * (0.42 + Math.random() * 0.14)
      bubble.y = rect.top + rect.height * (0.25 + Math.random() * 0.05)
      bubble.vx = (Math.random() - 0.5) * 210
      bubble.vy = -(26 + Math.random() * 74)
      bubble.size = rect.width * (0.055 + Math.random() * 0.085)
      bubble.scale = bubble.size * 0.2
      bubble.opacity = 0
      bubble.life = 0
      bubble.max = 3.4 + Math.random() * 3.2
      bubble.phase = Math.random() * 10
      bubble.popping = -1
      bubble.alive = true
      bubble.image = spriteImages[Math.floor(Math.random() * spriteImages.length)]!
    }

    function marchStep(delta: number, elapsed: number): void {
      // The card arrives long after the scene mounts, and re-renders when it
      // is edited, so the element is re-found rather than held.
      marchLook -= delta
      if (!marchCutout || !marchCutout.isConnected) {
        if (marchLook <= 0) {
          marchLook = 0.5
          const found = document.querySelector<HTMLElement>('.skin-collage__hero')
          if (found) {
            marchCutout = found
            marchWatch.observe(found)
          }
        }
      }

      const rect = marchActive && marchCutout ? marchCutout.getBoundingClientRect() : null
      if (rect && rect.width > 0) {
        marchDue += delta
        const gap = 0.11
        while (marchDue > gap) {
          marchDue -= gap
          marchSpawn(rect)
        }
      }

      for (const bubble of marchPool) {
        if (!bubble.alive) continue
        if (bubble.popping >= 0) {
          bubble.popping += delta
          const k = Math.min(1, bubble.popping / 0.09)
          bubble.scale = bubble.size * (1 + 0.45 * k)
          bubble.opacity = Math.max(0, 1 - k * k * 2.4) * 0.85
          if (k >= 1) bubble.alive = false
          continue
        }
        bubble.life += delta
        // Rising and slowing, the way the machine's own bubbles do.
        bubble.vy -= 26 * delta
        // Little lateral drag, so a bubble that leaves the crown sideways
        // keeps going and the card fills rather than a plume forming.
        bubble.vx *= 1 - 0.35 * delta
        bubble.vy *= 1 - 0.5 * delta
        bubble.x += bubble.vx * delta + Math.sin(elapsed * 1.7 + bubble.phase) * 0.5
        bubble.y += bubble.vy * delta
        const inflate = Math.min(1, bubble.life / 0.34)
        const eased = inflate * inflate * (3 - 2 * inflate)
        bubble.scale = bubble.size * (0.2 + 0.8 * eased) * (1 + 0.14 * (bubble.life / bubble.max))
        bubble.opacity = Math.min(1, bubble.life * 5) * 0.9
        if (bubble.life >= bubble.max || bubble.y + bubble.scale < 0) bubble.popping = 0
      }
    }

    function marchDraw(): void {
      for (const bubble of marchPool) {
        if (!bubble.alive) continue
        const half = (bubble.scale / 2) * overlayDpr
        if (half < 1) continue
        overlayCtx.globalAlpha = bubble.opacity
        overlayCtx.drawImage(
          bubble.image,
          bubble.x * overlayDpr - half,
          bubble.y * overlayDpr - half,
          half * 2,
          half * 2,
        )
      }
      overlayCtx.globalAlpha = 1
    }

    function stepBubbles(delta: number, elapsed: number): void {
      for (const bubble of bubbles) {
        if (!bubble.alive) continue

        if (bubble.popping >= 0) {
          // A real bubble does not fade out. The film thins, tears at a point
          // and the whole thing is gone inside a couple of frames, so the
          // opacity leaves faster than the shell expands: 90ms, and the last
          // half of it is nearly invisible.
          bubble.popping += delta
          const k = Math.min(1, bubble.popping / 0.09)
          bubble.scale = bubble.size * (1 + 0.45 * k)
          bubble.opacity = Math.max(0, 1 - k * k * 2.4) * 0.85
          if (k >= 1) bubble.alive = false
          continue
        }

        bubble.life += delta

        // Buoyancy, not gravity: a soap bubble is lighter than the air it
        // displaces, and drag dominates almost immediately, so it accelerates
        // for a moment and then drifts.
        bubble.velocity.y += 0.55 * delta
        bubble.velocity.multiplyScalar(1 - 1.4 * delta)
        bubble.position.addScaledVector(bubble.velocity, delta)
        bubble.position.x += Math.sin(elapsed * 1.9 + bubble.phase) * 0.009
        bubble.position.y += Math.cos(elapsed * 1.5 + bubble.phase) * 0.003

        // Inflate off the wand over the first third of a second, then keep
        // swelling very slightly as the film thins — which is also what makes
        // it burst, so the two are the same number.
        const inflate = Math.min(1, bubble.life / 0.32)
        const eased = inflate * inflate * (3 - 2 * inflate)
        const thinning = 1 + 0.14 * (bubble.life / bubble.max)
        bubble.scale = bubble.size * (0.18 + 0.82 * eased) * thinning

        // No fade-out before the pop. It holds full opacity and then goes,
        // because a bubble that dims for half a second before vanishing reads
        // as smoke.
        bubble.opacity = Math.min(1, bubble.life * 6) * 0.9

        // Roomier than the old stage: on the overlay a bubble can cross the
        // cards before its film gives out.
        if (bubble.life >= bubble.max || bubble.position.y > 7.5 || Math.abs(bubble.position.x) > 8) {
          bubble.popping = 0
        }
      }
    }

    // ── Scroll ───────────────────────────────────────────────────────────
    // Read rather than listened for: `scrollY` is free in a frame callback,
    // and the scrollable height is cached because reading it is not.
    // The machine's whole performance plays out over the opening scroll, not
    // over the document. Mapped to the page it was a turn and a half spread
    // across eleven thousand pixels: at any moment you saw a slice of a
    // rotation too slow to read as motion, which is why the page felt still.
    // A viewport and a half is roughly the reference's pinned hero distance,
    // and it is the stretch where the machine is actually on screen.
    const RUN = 1.6
    let scrollable = Math.max(1, window.innerHeight * RUN)
    const remeasure = (): void => {
      scrollable = Math.max(1, window.innerHeight * RUN)
    }
    window.addEventListener('resize', remeasure, { passive: true })
    onDispose(() => window.removeEventListener('resize', remeasure))

    let lastScroll = window.scrollY
    let velocity = 0
    let idle = 0
    let spinNow = 0
    let emitAccumulator = 0

    // A short burst at mount, so the machine is never first seen switched off.
    for (let i = 0; i < 10; i += 1) spawn(0.2)

    onDispose(() => {
      for (const thing of disposables) thing.dispose()
    })

    return (delta, elapsed) => {
      // The motion layer owns the pin that keeps the stage on screen, so when
      // it is running its reading is the authority: the two have to agree or
      // the machine performs after it has scrolled away. Without it — reduced
      // motion, a failed import, no GSAP — the scene measures scroll itself.
      const hero = readHeroScroll()
      let progress: number
      if (hero.driven) {
        progress = hero.progress
        velocity = velocity * 0.82 + hero.velocity * 0.18
      } else {
        const scroll = window.scrollY
        progress = Math.min(1, Math.max(0, scroll / scrollable))
        velocity = velocity * 0.9 + (scroll - lastScroll) * 0.6
        lastScroll = scroll
      }

      // Eased rather than linear. A constant map to scroll makes the machine
      // feel geared to the scrollbar; easing the ends makes it feel driven by
      // it, which is the difference between a slider and a performance.
      const eased = progress * progress * (3 - 2 * progress)

      // Two different jobs for the same axis.
      //
      // In the hero it is a look left and then right, never a turntable: a full
      // rotation spends a third of every cycle in side profile, the one angle
      // where the machine reads as neither a face nor a shape. Idle sway plus a
      // scroll term, 41 degrees from facing you at the very most.
      //
      // Docked it does not drift at all. It is pointed by how far down the
      // steps the reader is: front at the first, round to a full profile, back
      // through the front, and out to the other profile. A sine gives all four
      // turning points and arrives back at the front, and being proportional it
      // does not care how many cards there are.
      let target: number
      if (hero.docked) {
        target = Math.sin(hero.steps * Math.PI * 2) * (Math.PI / 2)
      } else {
        idle += delta
        target = Math.sin(idle * 0.42) * IDLE_YAW + (eased - 0.5) * 2 * SCROLL_YAW
      }
      spinNow += (target - spinNow) * Math.min(1, delta * (hero.docked ? 5 : 3.2))
      spin.rotation.y = spinNow

      // Both are null until the dynamic import lands, a frame or two in.
      if (head) {
        head.rotation.x =
          -0.30 + 0.40 * (0.5 - 0.5 * Math.cos(eased * Math.PI * 2)) + Math.sin(elapsed * 0.9) * 0.035
      }

      const speed = Math.min(1, Math.abs(velocity) / 40)
      // The wand turns behind its bezel, faster as the page moves, about the
      // head's own +Z — the axis the spec gives the wand pivot.
      if (disc) disc.rotation.z += delta * (1.6 + speed * 6)

      // Framed from the model rather than from constants. The distance that
      // just contains the framing sphere is computed against whichever of the
      // two field of view angles is tighter — on a tall narrow canvas that is
      // the horizontal one — so the machine is never cut off by an aspect the
      // numbers were not written for. Docking changes the aspect from 1.27 to
      // 1.0 without reloading anything, which is exactly that case.
      const vertical = (camera.fov * Math.PI) / 180
      const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * camera.aspect)
      const tightest = Math.min(vertical, horizontal)
      const contain = frame.radius / Math.sin(tightest / 2)

      const centreY = -0.85 + frame.centreY
      if (hero.docked) {
        // Square-on and a little above, with the smallest margin that still
        // clears the sphere. At four centimetres across there is no room for a
        // dramatic angle and nothing to gain from one.
        camera.position.set(0, centreY + contain * 0.16, contain * 1.02)
      } else {
        // Dollies in and drops as the machine turns. Both ends are multiples
        // of the containing distance, so the move cannot push it out of frame.
        const pull = 1.2 - eased * 0.2
        camera.position.set(
          Math.sin(eased * Math.PI) * contain * 0.06,
          centreY + contain * (0.34 - eased * 0.14),
          contain * pull,
        )
      }
      camera.lookAt(0, centreY, 0)

      // Emission is rate-based rather than per-frame, so a slow frame does not
      // silently blow fewer bubbles than a fast one.
      // Docked it blows a few, not a stream. The canvas is a fraction of the
      // area by then and every sprite is its own draw call, so this is the
      // cheapest lever the scene has and the one that matters most on a phone.
      const rate = hero.docked ? 3.5 : 5 + speed * 55
      emitAccumulator += rate * delta
      while (emitAccumulator >= 1) {
        spawn(speed)
        emitAccumulator -= 1
      }

      stepBubbles(delta, elapsed)
      drawBubbles()
      // After the machine's own, so the finale's bubbles are never cleared
      // by the pass that draws them.
      marchStep(delta, elapsed)
      marchDraw()
    }
  },

  // No WebGL, or the visitor asked for less motion: the same scene as a still.
  // Handled entirely in CSS — see `.skin-scene--still` in skin.css.
  reducedMotionFallback: (container) => {
    container.classList.add('skin-scene--still')
  },
}

export default scene
